// MAP-FIRE (owner 2026-11-02: 'explosions, fires, can char the ground or trees or start fires that grow or expand and burn
// the vegetation'). A coarse grass-fire grid over the arena: blasts, the Nova Lance path, Riftline, Flare Barrage pops and
// the odd Chain Arc strike ignite cells; burning cells spread to their neighbours for a few seconds, burn out to char that
// can't re-ignite for a while, and set trees and bushes alight (trees char, take burn damage and finally break with PROP_SRC.burn;
// a burning tree throws fire around itself, so it jumps tree to tree a little). Rocks only scorch-darken.
// Pure + deterministic: its own seeded Rng (never perturbs combat randomness), fixed DT steps, no three/DOM.
// Gameplay: fire never touches the player or foes — it is map destruction only.
import { Rng } from './util';
import { EventSink, isFlora, PROP_SRC, Terrain } from './terrain';

/** cell size (m) */
export const FIRE_CELL = 2;
/** hard cap on simultaneously burning cells */
export const MAX_BURNING = 160;
/** seconds a cell burns (random in range) */
export const FIRE_BURN_MIN = 4, FIRE_BURN_MAX = 8;
/** mean seconds for a burning cell to light a side neighbour (diagonals take twice as long) */
export const FIRE_SPREAD_T = 1.1;
/** seconds before burnt grass can catch again */
export const FIRE_REGROW = 45;
/** seconds a burning tree lasts before it breaks */
export const TREE_BURN_T = 8;
/** seconds a burning bush lasts before it breaks (dry brush: goes up fast) */
export const BUSH_BURN_T = 3.2;
/** a tree lit by fire of generation g throws generation g+1 fire; trees stop spreading past this generation */
export const TREE_GEN_MAX = 2;
const MAX_FIRES = 48;
const SPREAD_EVERY = 3; // ticks between spread rolls

export const CELL = { fresh: 0, burning: 1, burnt: 2, none: 3 } as const;

interface Fire { x: number; z: number; maxR: number; gen: number }

export class FireGrid {
  /** cells per side; cell (ix, iz) centre = (ix + 0.5) * FIRE_CELL - half */
  readonly n: number;
  readonly half: number;
  /** CELL state per cell */
  readonly state: Uint8Array;
  /** burning: seconds alight; burnt: seconds since it went out */
  readonly t: Float32Array;
  /** burn duration of each cell (s) */
  readonly life: Float32Array;
  private fireOf: Uint8Array;
  /** indices of the burning cells (render reads; ≤ MAX_BURNING) */
  readonly burning: number[] = [];
  private burnt: number[] = [];
  private fires: Fire[] = [];
  private nextFire = 0;
  private treeGen: Int8Array;
  private trees: number[] = [];
  private rng: Rng;
  private tick = 0;
  /** total cells ever lit (tests / QA) */
  lit = 0;

  /** enabled = false: nothing on this map catches fire (map flag MapCoreDef.fire); ignite() is a no-op.
   *  mask (MapCoreDef.fireMask): where the ground can burn (e.g. Ember Wastes: only the dry ash-grass patches, never bare
   *  basalt); cells outside it never catch or carry fire. Trees and bushes still burn wherever they stand: the cells under
   *  their own footprint stay burnable, so a lit bush on basalt burns out without spreading across the rock. */
  constructor(seed: number, readonly arenaR: number, terrain: Terrain, readonly enabled = true, mask?: (x: number, z: number) => boolean) {
    this.rng = new Rng((seed * 104729 + 31) >>> 0);
    this.n = Math.ceil((arenaR * 2) / FIRE_CELL); this.half = (this.n * FIRE_CELL) / 2;
    const N = this.n * this.n;
    this.state = new Uint8Array(N); this.t = new Float32Array(N); this.life = new Float32Array(N); this.fireOf = new Uint8Array(N);
    for (let k = 0; k < MAX_FIRES; k++) this.fires.push({ x: 0, z: 0, maxR: 0, gen: 0 });
    // outside the arena, and under boulders / spires, nothing burns
    for (let i = 0; i < N; i++) { const x = this.cx(i), z = this.cz(i); if (x * x + z * z > (arenaR - 1) ** 2 || (mask && !mask(x, z))) this.state[i] = CELL.none; }
    if (mask) for (const p of terrain.props) { // flora is fuel wherever it stands (its own cell, not the bare ground around it)
      if (!isFlora(p.kind)) continue;
      const c = this.cellAt(p.x, p.z); if (c >= 0 && (p.x * p.x + p.z * p.z) <= (arenaR - 1) ** 2) this.state[c] = CELL.fresh;
    }
    terrain.props.forEach((p, i) => {
      if (isFlora(p.kind)) { this.trees.push(i); return; } // trees + bushes burn
      if (!p.solid) return;
      const r = Math.ceil(p.r / FIRE_CELL);
      const c = this.cellAt(p.x, p.z); if (c < 0) return;
      const ix = c % this.n, iz = Math.floor(c / this.n);
      for (let a = -r; a <= r; a++) for (let b = -r; b <= r; b++) {
        const jx = ix + a, jz = iz + b; if (jx < 0 || jz < 0 || jx >= this.n || jz >= this.n) continue;
        const j = jz * this.n + jx; if ((this.cx(j) - p.x) ** 2 + (this.cz(j) - p.z) ** 2 < (p.r * 0.85) ** 2) this.state[j] = CELL.none;
      }
    });
    this.treeGen = new Int8Array(terrain.props.length).fill(-1);
  }

  cx(i: number): number { return ((i % this.n) + 0.5) * FIRE_CELL - this.half; }
  cz(i: number): number { return (Math.floor(i / this.n) + 0.5) * FIRE_CELL - this.half; }
  /** CELL state under (x, z) (CELL.none outside the grid) — Scorched Earth trait reads it */
  stateAt(x: number, z: number): number { const c = this.cellAt(x, z); return c < 0 ? CELL.none : this.state[c]; }
  cellAt(x: number, z: number): number {
    const ix = Math.floor((x + this.half) / FIRE_CELL), iz = Math.floor((z + this.half) / FIRE_CELL);
    return ix < 0 || iz < 0 || ix >= this.n || iz >= this.n ? -1 : iz * this.n + ix;
  }
  /** a deterministic chance roll on the fire's own Rng */
  roll(p: number): boolean { return p >= 1 || (p > 0 && this.rng.next() < p); }
  /** random number on the fire's own Rng (sim callers pick ignition spots with it) */
  rand(): number { return this.rng.next(); }

  /** Light the grass within r of (x, z); the fire may then spread up to maxR from here. gen = tree-jump generation.
   *  Returns the cells lit. */
  ignite(x: number, z: number, r: number, maxR: number, gen = 0): number {
    if (!this.enabled) return 0;
    const slot = this.nextFire; this.nextFire = (this.nextFire + 1) % MAX_FIRES;
    const f = this.fires[slot]; f.x = x; f.z = z; f.maxR = maxR; f.gen = gen;
    const c = this.cellAt(x, z); if (c < 0) return 0;
    const rr = Math.max(0, Math.ceil(r / FIRE_CELL)), ix = c % this.n, iz = Math.floor(c / this.n);
    let lit = 0;
    for (let b = -rr; b <= rr; b++) for (let a = -rr; a <= rr; a++) {
      const jx = ix + a, jz = iz + b; if (jx < 0 || jz < 0 || jx >= this.n || jz >= this.n) continue;
      const j = jz * this.n + jx;
      if ((a || b) && (this.cx(j) - x) ** 2 + (this.cz(j) - z) ** 2 > r * r) continue;
      if (this.light(j, slot)) lit++;
    }
    return lit;
  }

  private light(j: number, slot: number): boolean {
    if (this.state[j] !== CELL.fresh || this.burning.length >= MAX_BURNING) return false;
    this.state[j] = CELL.burning; this.t[j] = 0; this.life[j] = FIRE_BURN_MIN + (FIRE_BURN_MAX - FIRE_BURN_MIN) * this.rng.next();
    this.fireOf[j] = slot; this.burning.push(j); this.lit++;
    return true;
  }

  /** deterministic per-cell jitter 0..1 (ragged fire edges) */
  private hash(j: number): number { const h = Math.sin(j * 12.9898 + 78.233) * 43758.5453; return h - Math.floor(h); }

  /** One fixed sim tick. */
  update(dt: number, terrain: Terrain, out: EventSink): void {
    this.tick++;
    const B = this.burning, n = this.n;
    // age the burning cells; burnt-out cells turn to char
    for (let k = B.length - 1; k >= 0; k--) {
      const j = B[k]; this.t[j] += dt;
      if (this.t[j] >= this.life[j]) { this.state[j] = CELL.burnt; this.t[j] = 0; B[k] = B[B.length - 1]; B.pop(); this.burnt.push(j); }
    }
    // spread to neighbours (rolled every few ticks), never past the fire's ragged max radius
    if (this.tick % SPREAD_EVERY === 0) {
      const p1 = (SPREAD_EVERY * dt) / FIRE_SPREAD_T, p2 = p1 * 0.5, len = B.length;
      for (let k = 0; k < len && B.length < MAX_BURNING; k++) {
        const j = B[k], age = this.t[j];
        if (age < 0.35 || age > this.life[j] * 0.8) continue; // a fresh fire takes hold first; a dying one no longer spreads
        const f = this.fires[this.fireOf[j]], ix = j % n, iz = Math.floor(j / n);
        for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
          if (!a && !b) continue;
          const jx = ix + a, jz = iz + b; if (jx < 0 || jz < 0 || jx >= n || jz >= n) continue;
          const q = jz * n + jx; if (this.state[q] !== CELL.fresh) continue;
          if (this.rng.next() >= (a && b ? p2 : p1)) continue;
          const d2 = (this.cx(q) - f.x) ** 2 + (this.cz(q) - f.z) ** 2, lim = f.maxR * (0.72 + 0.4 * this.hash(q));
          if (d2 > lim * lim) continue;
          this.light(q, this.fireOf[j]);
        }
      }
    }
    // burnt grass regrows slowly
    if (this.tick % 60 === 0) {
      const R = this.burnt;
      for (let k = R.length - 1; k >= 0; k--) {
        const j = R[k]; this.t[j] += 60 * dt;
        if (this.t[j] >= FIRE_REGROW) { this.state[j] = CELL.fresh; this.t[j] = 0; R[k] = R[R.length - 1]; R.pop(); }
      }
    }
    // trees in burning grass catch fire; burning trees char, lose health, throw fire around them, and finally break
    const props = terrain.props;
    for (const i of this.trees) {
      const p = props[i]; if (!p.alive) continue;
      if (p.burn <= 0) {
        if ((this.tick + i) % 6 !== 0 || !B.length) continue;
        const c = this.cellAt(p.x, p.z); if (c < 0 || this.state[c] !== CELL.burning) continue;
        p.burn = 1e-3; this.treeGen[i] = this.fires[this.fireOf[c]].gen;
        continue;
      }
      const bush = p.kind === 'bush', T = bush ? BUSH_BURN_T : TREE_BURN_T;
      // owner 2026-11-02: 'trees keep turning brown the moment they are hit without being on fire' — the char now only starts
      // once the crown flames have grown in (render: grow = burn / 1.5 s tree, 0.6 s bush), so a tree never browns before it visibly burns
      const lead = bush ? 0.6 : 1.5;
      p.burn += dt; p.char = Math.min(1, Math.max(0, p.burn - lead) / (T * 0.6 - lead));
      p.hp -= (p.maxHp / T) * dt;
      // a burning bush throws a smaller fire, but sooner (dry brush carries a grass fire along)
      if ((this.tick + i) % (bush ? 40 : 60) === 0 && this.treeGen[i] < TREE_GEN_MAX) this.ignite(p.x, p.z, bush ? 1.6 : 2.2, (bush ? 3.5 : 4.5) + this.rng.next() * 2.5, this.treeGen[i] + 1);
      if (p.hp <= 0) {
        p.hp = 1e-3; const a = this.rng.next() * Math.PI * 2, dx = Math.cos(a), dz = Math.sin(a);
        terrain.hit(p, i, 1, out, p.x - dx, p.z - dz, PROP_SRC.burn, dx, dz);
      }
    }
    // rocks in burning grass get scorched (render darkens them a little)
    if (this.tick % 6 === 0 && B.length) {
      for (const p of props) {
        if (!p.alive || isFlora(p.kind) || p.char >= 0.45) continue;
        const c = this.cellAt(p.x + p.r + 0.6, p.z), d = this.cellAt(p.x - p.r - 0.6, p.z), e = this.cellAt(p.x, p.z + p.r + 0.6), f = this.cellAt(p.x, p.z - p.r - 0.6);
        if ((c >= 0 && this.state[c] === CELL.burning) || (d >= 0 && this.state[d] === CELL.burning) || (e >= 0 && this.state[e] === CELL.burning) || (f >= 0 && this.state[f] === CELL.burning)) p.char = Math.min(0.45, p.char + 0.05);
      }
    }
  }
}
